import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";

type AppRole = 'super_admin' | 'tournament_manager' | 'finance_manager' | 'team_coordinator' | 'viewer';

interface UserRoleContextType {
  user: User | null;
  role: AppRole | null;
  loading: boolean;
  hasRole: (requiredRole: AppRole) => boolean;
  canEdit: boolean;
  canManageUsers: boolean;
  canViewConfig: boolean;
}

const UserRoleContext = createContext<UserRoleContextType | undefined>(undefined);

export const useUserRole = () => {
  const context = useContext(UserRoleContext);
  if (!context) {
    throw new Error("useUserRole must be used within UserRoleProvider");
  }
  return context;
};

export const UserRoleProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserRole(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserRole(session.user.id);
      } else {
        setRole(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .order("role", { ascending: true });

      if (error) throw error;

      // Set the highest priority role (super_admin > tournament_manager > finance_manager > team_coordinator > viewer)
      if (data && data.length > 0) {
        const roles = data.map(r => r.role);
        if (roles.includes('super_admin')) {
          setRole('super_admin');
        } else if (roles.includes('tournament_manager')) {
          setRole('tournament_manager');
        } else if (roles.includes('finance_manager')) {
          setRole('finance_manager');
        } else if (roles.includes('team_coordinator')) {
          setRole('team_coordinator');
        } else if (roles.includes('viewer')) {
          setRole('viewer');
        } else {
          setRole(null);
        }
      } else {
        setRole(null);
      }
    } catch (error) {
      console.error("Error fetching user role:", error);
      setRole(null);
    } finally {
      setLoading(false);
    }
  };

  const hasRole = (requiredRole: AppRole): boolean => {
    if (!role) return false;
    
    // Define role hierarchy
    const roleHierarchy: Record<AppRole, number> = {
      'super_admin': 5,
      'tournament_manager': 4,
      'finance_manager': 3,
      'team_coordinator': 2,
      'viewer': 1,
    };
    
    return roleHierarchy[role] >= roleHierarchy[requiredRole];
  };

  const canEdit = hasRole('tournament_manager');
  const canManageUsers = hasRole('super_admin');
  const canViewConfig = hasRole('tournament_manager');

  return (
    <UserRoleContext.Provider
      value={{
        user,
        role,
        loading,
        hasRole,
        canEdit,
        canManageUsers,
        canViewConfig,
      }}
    >
      {children}
    </UserRoleContext.Provider>
  );
};
